import React, { useMemo } from 'react';
import { StrategySummary, simulatePitNow, TeamStrategy } from '../analytics/strategy';
import './StrategyPanel.css';

interface StrategyPanelProps {
  summary: StrategySummary;
  /** Highlighted teams, shown as "ours" with a pit-now call. */
  ourTeamIds: Set<string>;
}

/** `M:SS` for a duration in seconds — pit losses are read as minutes and seconds. */
function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  const whole = Math.round(seconds);
  const m = Math.floor(whole / 60);
  const s = whole % 60;
  return m > 0 ? `${m}:${s.toString().padStart(2, '0')}` : `${s}s`;
}

/** Signed seconds, so a projected gain reads as a negative gap. */
function formatGap(seconds: number | null): string {
  if (seconds === null) return '—';
  if (Math.abs(seconds) < 0.05) return '0.0';
  const sign = seconds > 0 ? '+' : '−';
  return `${sign}${Math.abs(seconds).toFixed(1)}`;
}

function formatMinutes(minutes: number | null): string {
  if (minutes === null) return '—';
  return `${Math.round(minutes)}′`;
}

function formatPace(seconds: number | null): string {
  if (seconds === null) return '—';
  const m = Math.floor(seconds / 60);
  const s = (seconds % 60).toFixed(3);
  return m > 0 ? `${m}:${s.padStart(6, '0')}` : s;
}

const PositionDelta: React.FC<{ delta: number | null }> = ({ delta }) => {
  if (delta === null || delta === 0) {
    return <span className="sp-delta sp-delta--flat">—</span>;
  }
  const gained = delta > 0;
  return (
    <span className={`sp-delta ${gained ? 'sp-delta--up' : 'sp-delta--down'}`}>
      {gained ? '▲' : '▼'}
      {Math.abs(delta)}
    </span>
  );
};

/** Colour the stint countdown the same way the pit window does. */
function stintClass(remaining: number | null, dueSoon: number): string {
  if (remaining === null) return '';
  if (remaining < 0) return 'sp-stint--over';
  if (remaining <= dueSoon) return 'sp-stint--soon';
  return '';
}

const OurKart: React.FC<{
  row: TeamStrategy;
  summary: StrategySummary;
  dueSoonMinutes: number;
}> = ({ row, summary, dueSoonMinutes }) => {
  const pitNow = useMemo(
    () => simulatePitNow(summary, row.teamId),
    [summary, row.teamId],
  );

  return (
    <div className="sp-card">
      <div className="sp-card-head">
        <span className="sp-kart">#{row.kartNumber}</span>
        <span className="sp-label">{row.label}</span>
        <span className="sp-proj">
          P{row.currentPosition ?? '—'}
          <span className="sp-arrow">→</span>
          P{row.projectedPosition ?? '—'}
        </span>
        <PositionDelta delta={row.positionDelta} />
      </div>

      <div className="sp-card-line">
        {row.stintRemainingMinutes !== null && (
          <span
            className={`sp-pill ${stintClass(row.stintRemainingMinutes, dueSoonMinutes)}`}
          >
            {row.stintRemainingMinutes < 0
              ? `${Math.abs(Math.round(row.stintRemainingMinutes))}′ over`
              : `${Math.round(row.stintRemainingMinutes)}′ of stint left`}
          </span>
        )}
        <span className="sp-pill">
          {row.stopsRemaining === null
            ? 'stops unknown'
            : row.stopsRemaining === 0
              ? 'can run to the flag'
              : `${row.stopsRemaining} stop${row.stopsRemaining === 1 ? '' : 's'} to go`}
        </span>
      </div>

      <div className="sp-card-line sp-pitnow">
        {pitNow.rejoinPosition === null ? (
          <span className="sp-muted">
            No pit-loss measured yet — waiting on the first stop.
          </span>
        ) : (
          <>
            <span className="sp-pitnow-label">Pit now →</span>
            <span className="sp-pitnow-body">
              rejoin P{pitNow.rejoinPosition}
              {pitNow.behind && <> behind #{pitNow.behind.kartNumber}</>}
              {pitNow.ahead && <>, ahead of #{pitNow.ahead.kartNumber}</>}
              <span className="sp-muted"> ({formatDuration(pitNow.lossSeconds)} lost)</span>
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export const StrategyPanel: React.FC<StrategyPanelProps> = ({
  summary,
  ourTeamIds,
}) => {
  const ourRows = summary.teams.filter((t) => ourTeamIds.has(t.teamId));

  // Projected order is the whole point of the table, so lead with it and keep
  // the current position alongside for reference.
  const projected = useMemo(
    () =>
      summary.teams
        .filter((t) => t.projectedPosition !== null)
        .sort((a, b) => a.projectedPosition! - b.projectedPosition!),
    [summary.teams],
  );

  const dueSoonMinutes = 10;

  return (
    <div className="sp-panel">
      <div className="sp-header">
        <h3>Strategy</h3>
        <div className="sp-stats">
          <span className="sp-stat">
            <b>{formatMinutes(summary.minutesRemaining)}</b> left
          </span>
          <span className="sp-stat">
            <b>{formatDuration(summary.pitLossSeconds)}</b> pit loss
            {summary.pitLossSource && (
              <span className="sp-muted">
                {' '}
                ({summary.pitLossSource === 'team' ? 'our stops' : 'field median'})
              </span>
            )}
          </span>
        </div>
      </div>

      {ourRows.length === 0 ? (
        <div className="sp-empty">
          Click a row in the grid to mark a kart as yours — its pit call and
          projection appear here.
        </div>
      ) : (
        <div className="sp-cards">
          {ourRows.map((row) => (
            <OurKart
              key={row.teamId}
              row={row}
              summary={summary}
              dueSoonMinutes={dueSoonMinutes}
            />
          ))}
        </div>
      )}

      {projected.length > 0 && (
        <div className="sp-table-wrap">
          <table className="sp-table">
            <thead>
              <tr>
                <th>Proj</th>
                <th>Now</th>
                <th />
                <th>Kart</th>
                <th>Team</th>
                <th>Pace</th>
                <th>Stint</th>
                <th>Stops</th>
                <th>Gap @ flag</th>
              </tr>
            </thead>
            <tbody>
              {projected.map((row) => (
                <tr
                  key={row.teamId}
                  className={ourTeamIds.has(row.teamId) ? 'sp-row--ours' : undefined}
                >
                  <td className="sp-num">{row.projectedPosition}</td>
                  <td className="sp-num sp-muted">{row.currentPosition ?? '—'}</td>
                  <td>
                    <PositionDelta delta={row.positionDelta} />
                  </td>
                  <td className="sp-num">{row.kartNumber}</td>
                  <td className="sp-team">{row.label}</td>
                  <td className="sp-num">{formatPace(row.paceSeconds)}</td>
                  <td
                    className={`sp-num ${stintClass(row.stintRemainingMinutes, dueSoonMinutes)}`}
                  >
                    {row.stintRemainingMinutes === null
                      ? '—'
                      : `${Math.round(row.stintRemainingMinutes)}′`}
                  </td>
                  <td className="sp-num">{row.stopsRemaining ?? '—'}</td>
                  <td className="sp-num">{formatGap(row.projectedGapSeconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {projected.length === 0 && (
        <div className="sp-empty">
          Projections need a session countdown and a few laps of pace.
        </div>
      )}
    </div>
  );
};

export default StrategyPanel;
